import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, Maximize2, BedDouble, Wifi, Wind, Tv, Coffee, Droplets, Refrigerator, CookingPot } from 'lucide-react';
import { formatCurrency, getImageUrl } from '../utils/helpers';

// Memetakan icon string dari JSON ke komponen Lucide Icons
const AMENITY_ICONS = { 
  wifi: Wifi, 
  ac: Wind, 
  tv: Tv, 
  coffee: Coffee,
  fridge: Refrigerator,
  kettle: Coffee, // Menggunakan icon kopi untuk ketel
  wardrobe: Maximize2, // Representasi lemari
  linen: BedDouble, // Representasi seprai
  terrace: Coffee, 
  balcony: Coffee,
  kitchen: CookingPot
};

// --- FUNGSI PENGHITUNG MUSIM (HARGA DINAMIS BERDASARKAN GAMBAR) ---
const getSeasonMultiplier = (date) => {
  const month = date.getMonth(); // 0 = Jan, 1 = Feb, ..., 11 = Des
  const day = date.getDate();

  // 🔴 PEAK SEASON (Normal + 30.3% = 1.303)
  if (month === 2 && day >= 17 && day <= 25) return 1.303; // 17-Mar s/d 25-Mar (Libur Nyepi & Idul Fitri)
  if (month === 6 || month === 7) return 1.303; // 1-Jul s/d 31-Aug (International Holiday)
  if (month === 11 && day >= 23 && day <= 31) return 1.303; // 23-Dec s/d 31-Dec (Libur Natal & Tahun Baru)

  // 🟡 HIGH SEASON (Normal + 15.15% = 1.1515)
  if (month === 1 && day >= 14 && day <= 18) return 1.1515; // 14-Feb s/d 18-Feb (Long Weekend Imlek)
  if (month === 4 && day >= 14 && day <= 17) return 1.1515; // 14-May s/d 17-May (Long Weekend Kenaikan Isa Al Masih)
  if (month === 4 && day >= 27 && day <= 31) return 1.1515; // 27-May s/d 31-May (Libur Idul Adha)
  if (month === 5) return 1.1515; // 1-Jun s/d 30-Jun (International Holiday)
  if (month === 8) return 1.1515; // 1-Sep s/d 30-Sep (International Holiday)

  // 🟢 NORMAL SEASON (Normal = 1.0)
  return 1.0; 
};

export default function RoomCard({ room, featured = false }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // ── LOGIKA SMART LANGUAGE (Mendukung ID, EN, & ZH) ──
  const name = lang === 'zh' 
    ? (room.name_zh || room.name_en || room.name_id)
    : lang === 'en' 
      ? (room.name_en || room.name_id) 
      : room.name_id;

  const description = lang === 'zh' 
    ? (room.description_zh || room.description_en || room.description_id)
    : lang === 'en' 
      ? (room.description_en || room.description_id) 
      : room.description_id;

  const bedType = lang === 'zh'
    ? (room.bed_type_zh || room.bed_type_en || room.bed_type_id)
    : lang === 'en'
      ? (room.bed_type_en || room.bed_type_id)
      : room.bed_type_id;

  // ── HITUNG HARGA DINAMIS HARI INI ──
  const todayMultiplier = getSeasonMultiplier(new Date());
  const rawPrice = room.price_per_night * todayMultiplier;
  
  // Rumus pembulatan ke ribuan terdekat (379.995 / 1000 = 379.995 -> dibulatkan jadi 380 -> dikali 1000 = 380.000)
  const todayPrice = Math.round(rawPrice / 1000) * 1000;

  return (
    <div className={`group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-earth-100 ${featured ? 'ring-2 ring-earth-600 ring-offset-2' : ''}`}>

      {/* ── Image ───────────────────────────────────────── */}
      <div className="relative overflow-hidden h-56">
        <img
          src={getImageUrl(room.main_image)}
          alt={name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        {featured && (
          <div className="absolute top-3 left-3 px-2.5 py-1 bg-accent-500 text-white rounded-full text-xs font-sans font-semibold shadow-md">
            Featured
          </div>
        )}
      </div>

      {/* ── Content ─────────────────────────────────────── */}
      <div className="p-5">
        <h3 className="font-display text-xl text-dark font-bold mb-1.5 group-hover:text-primary-700 transition-colors">
          {name}
        </h3>
        <p className="text-gray-500 text-sm font-sans leading-relaxed line-clamp-2 mb-4">
          {description}
        </p>

        {/* Stats */}
        <div className="flex items-center gap-4 text-xs text-gray-500 font-sans mb-4">
          <span className="flex items-center gap-1">
            <Maximize2 size={13} className="text-primary-600" />
            {room.size_sqm} {t('rooms.sqm')}
          </span>
          <span className="flex items-center gap-1">
            <Users size={13} className="text-primary-600" />
            {room.capacity} {t('rooms.guests_label')}
          </span>
          <span className="flex items-center gap-1">
            <BedDouble size={13} className="text-primary-600" />
            {bedType}
          </span>
        </div>

        {/* Amenities (Menampilkan maksimal 4 icon fasilitas pertama) */}
        <div className="flex gap-2 mb-4">
          {(room.amenities || []).slice(0, 4).map((amenity) => {
            const Icon = AMENITY_ICONS[amenity.icon] || Wifi;
            return (
              <div
                key={amenity.id}
                title={lang === 'zh' ? (amenity.name_zh || amenity.name_en) : (lang === 'en' ? amenity.name_en : amenity.name_id)}
                className="w-7 h-7 rounded-lg bg-primary-50 flex items-center justify-center text-primary-700"
              >
                <Icon size={13} />
              </div>
            );
          })}
        </div>

        {/* Price + CTA */}
        <div className="flex items-end justify-between pt-4 border-t border-earth-100">
          <div>
            <span className="text-xs text-gray-400 font-sans">{t('rooms.from')}</span>
            <div className="text-primary-800 font-display text-2xl font-bold leading-none mt-1">
              {/* SEBELUMNYA */}
{/* {formatCurrency(Math.round(todayPrice))} */}

{/* GANTI MENJADI SEPERTI INI */}
<div className="text-primary-800 font-display text-2xl font-bold leading-none mt-1">
  {formatCurrency(todayPrice)}
</div>
            </div>
            <span className="text-xs text-gray-400 font-sans">{t('rooms.per_night')}</span>
          </div>
          
          <Link
            to={`/rooms/${room.slug}`}
            className="px-5 py-2.5 rounded-xl text-sm font-sans font-semibold transition-all duration-200 bg-primary-700 hover:bg-primary-600 text-white hover:shadow-lg hover:shadow-primary-900/20"
          >
            {t('rooms.view_more')}
          </Link>
        </div>
      </div>
    </div>
  );
}